import { useContext } from 'react';

import { Box, Card, Icon, Image, Text } from '@zillow/constellation-10';
import { IconArrowRightFilled, IconBuildingFilled, IconDoorOpenFilled } from '@zillow/constellation-icons';

import stringHelper from '../../../utils/stringHelper';

import { DISPLAY_SERVER_ERROR_AT, EXIT_CTA_LABEL } from '../../../constants/WizardConstants';

import { ModularListingContext } from '../../../containers/CreateListingPage/context';

import celebrateIllustration from '../../../images/celebrate.svg';

import PageWizard from '../../Wizard/PageWizard';
import WizardAlert from '../../WizardAlert';

import { type KeyboardEvent } from 'react';
import type { WizardBodyTemplateProps } from '../../Wizard/types';

// TODO: After BE api is complete:
// - After successful publish, GET unitPublished: /api/web/v1/users/properties/multiplex/unitPublished?multiplexBuildingSessionId={sessionId}&listingAlias={publishedListingAlias}
// - DTO: MultiplexUnitPublishedResponseDto — publishedUnit, unpublishedCount, nextUnit (listingAlias, unitNumber, bedrooms, bathrooms) | null
// - Body: Unit {publishedUnit.unitNumber} will be live…; append "You have …" only when hasUnpublishedUnits (unpublishedCount != null && > 0) using stringHelper.pluralize for unit/units; omit that clause when unpublishedCount is 0 or null.
// - Next card ("Next unit to list"): show when hasUnpublishedUnits (and when wired, also require nextUnit != null); onClick → ?step=review&multiplexBuildingSessionId=… (useMultiplexListingFlow / isInMultiplexListingFlow).
// - Back card: ?multiplexBuildingSessionId={id}&step=MULTIPLEX_OVERVIEW&isComingFromReview=true → MultiplexOverview; always show (or adjust copy when all units published).
// - All published: nextUnit null and/or unpublishedCount 0 or null → no "You have …" tail; hide Next card; keep Back / overview path.

const MultiplexListingPublished = (wizardProps: WizardBodyTemplateProps) => {
  const { onGoToNextStep: _wizardNextOmitted, onGoToPrevStep: _wizardPrevOmitted, ...props } = wizardProps;
  const {
    onExitWizard,
    wizardTitle,
    progressStepper,
    totalSteps,
    currentStepIndex,
    showDesktopViewBreakpoint,
    ...rest
  } = props;

  const {
    modularListingState: { serverErrorObj },
  } = useContext(ModularListingContext);

  // TODO: publishedUnit.unitNumber & unpublishedCount from unitPublished (MultiplexUnitPublishedResponseDto)
  const publishedUnitNumber = 1 as number;
  const unpublishedCount: number | null = 4;

  const hasUnpublishedUnits = unpublishedCount != null && unpublishedCount > 0;

  const handleListNextUnitClick = () => {
    // TODO: route to Review for nextUnit: /rental-manager/properties/{listingAlias}/create-listing?multiplexBuildingSessionId={id}&step=review
  };

  const handleBackToPropertyClick = () => {
    // TODO: "Back to bulk property overview" → /create-listing?multiplexBuildingSessionId={id}&step=MULTIPLEX_OVERVIEW&isComingFromReview=true
  };

  const handleButtonKeyDown = (event: KeyboardEvent<HTMLDivElement>, action: () => void) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      action();
    }
  };

  return (
    <PageWizard
      title=""
      wizardTitle={wizardTitle}
      progressStepper={progressStepper}
      totalSteps={totalSteps}
      currentStepIndex={currentStepIndex}
      exitCTALabel={EXIT_CTA_LABEL.SAVE_AND_EXIT}
      onExitTriggered={onExitWizard}
      showDesktopViewBreakpoint={showDesktopViewBreakpoint}
      hideFooter={true}
      {...rest}
    >
      <Box
        css={{
          margin: '0 auto',
          maxWidth: '500px',
          width: '100%',
        }}
        data-testid="MultiplexListingPublished"
      >
        <WizardAlert serverErrorObj={serverErrorObj} displayAt={DISPLAY_SERVER_ERROR_AT.PAGE} />

        <Box
          css={{
            display: 'flex',
            flexDirection: 'column',
            gap: '400',
            justifyContent: 'center',
          }}
        >
          <Box
            data-testid="HeaderContainer"
            css={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '400',
              justifyContent: 'center',
              textAlign: 'center',
            }}
          >
            <Image
              src={celebrateIllustration}
              alt="Celebration illustration"
              css={{ width: '120px', height: '120px', flexShrink: 0, objectFit: 'cover' }}
            />

            <Text textStyle="heading-md">Listing published</Text>

            {/* body: publishedUnit line; append "You have …" only when hasUnpublishedUnits (see file header) */}
            <Text textStyle="body">
              {`Unit ${publishedUnitNumber} will be live on the Zillow Rental Network soon.${
                hasUnpublishedUnits
                  ? `  You have ${stringHelper.pluralize(
                      unpublishedCount,
                      'more unit to setup',
                      'more units to setup',
                    )}.`
                  : ''
              }`}
            </Text>
          </Box>

          <Box
            data-testid="UnitListContainer"
            css={{
              display: 'flex',
              flexDirection: 'column',
              alignSelf: 'stretch',
              gap: '200',
            }}
          >
            <Box css={{ display: 'flex', flexDirection: 'column', gap: '200' }}>
              {hasUnpublishedUnits ? (
                <Box
                  role="button"
                  tabIndex={0}
                  onClick={handleListNextUnitClick}
                  onKeyDown={(event) => handleButtonKeyDown(event, handleListNextUnitClick)}
                  css={{
                    all: 'unset',
                    display: 'block',
                    width: '100%',
                    cursor: 'pointer',
                  }}
                >
                  <Card
                    data-testid="NextUnitCard"
                    tone="neutral"
                    elevated={false}
                    css={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: '200',
                      width: '100%',
                      paddingBlock: '300',
                      paddingInline: '400',
                      textAlign: 'left',
                    }}
                  >
                    <Box
                      css={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: '300',
                        minWidth: 0,
                        flex: 1,
                      }}
                    >
                      <Box css={{ flexShrink: 0 }} aria-hidden={true}>
                        <Icon size="lg" css={{ color: 'icon.accent.blue.hero' }} render={<IconDoorOpenFilled />} />
                      </Box>
                      <Box
                        css={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '25',
                          minWidth: 0,
                        }}
                      >
                        <Text textStyle="body-bold" css={{ color: 'text.default' }}>
                          List unit 2 next
                        </Text>
                        <Text textStyle="body-sm" css={{ color: 'text.subtle' }}>
                          Continue setting up your next unit’s listing
                        </Text>
                      </Box>
                    </Box>
                    <Box css={{ flexShrink: 0 }} aria-hidden={true}>
                      <Icon size="lg" css={{ color: 'icon.neutral' }} render={<IconArrowRightFilled />} />
                    </Box>
                  </Card>
                </Box>
              ) : null}

              <Box
                role="button"
                tabIndex={0}
                onClick={handleBackToPropertyClick}
                onKeyDown={(event) => handleButtonKeyDown(event, handleBackToPropertyClick)}
                css={{
                  all: 'unset',
                  display: 'block',
                  width: '100%',
                  cursor: 'pointer',
                }}
              >
                <Card
                  data-testid="BackToPropertyCard"
                  tone="neutral"
                  elevated={false}
                  css={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '200',
                    width: '100%',
                    paddingBlock: '300',
                    paddingInline: '400',
                    textAlign: 'left',
                  }}
                >
                  <Box
                    css={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: '300',
                      minWidth: 0,
                      flex: 1,
                    }}
                  >
                    <Box css={{ flexShrink: 0 }} aria-hidden={true}>
                      <Icon size="lg" css={{ color: 'icon.neutral' }} render={<IconBuildingFilled />} />
                    </Box>
                    <Box
                      css={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '25',
                        minWidth: 0,
                      }}
                    >
                      <Text textStyle="body-bold" css={{ color: 'text.default' }}>
                        Back to property
                      </Text>
                      <Text textStyle="body-sm" css={{ color: 'text.subtle' }}>
                        View all units and manage your property
                      </Text>
                    </Box>
                  </Box>
                  <Box css={{ flexShrink: 0 }} aria-hidden={true}>
                    <Icon size="lg" css={{ color: 'icon.neutral' }} render={<IconArrowRightFilled />} />
                  </Box>
                </Card>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </PageWizard>
  );
};

export default MultiplexListingPublished;
